import Phaser from '../lib/phaser.js';

/**
 * @typedef AnimateTextConfig
 * @type {object}
 * @property {() => void} [callback]
 * @property {number} [delay=25]
 */

/**
 *
 * @param {Phaser.Scene} scene the Phaser 3 Scene the time event will be added to
 * @param {Phaser.GameObjects.Text} target the Phaser 3 Text Game Object that will be animated
 * @param {string} text the text to display on the target game object
 * @param {AnimateTextConfig} [config]
 * @returns {void}
 */
export function animateText(scene, target, text, config) {
  const length = (text ?? '').length;
  // Empty or single-char text: the per-char timer can't run safely — length 0 → `repeat: -1` is an
  // INFINITE Phaser timer and the callback never fires, so any battle message gate that showed a
  // short/empty string froze forever with input waiting (the "stuck after throwing a ball" bug).
  // Show it instantly and fire the callback now.
  if (length <= 1) {
    target.text = text ?? '';
    config?.callback?.();
    return { skip: () => {}, get done() { return true; } };
  }
  let i = 0;
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    target.text = text;
    // Fire on the LAST char (was `length - 1` — an off-by-one that fired one char early and NEVER
    // fired for length 1). Now the callback ALWAYS runs once the text is fully shown.
    config?.callback?.();
  };
  const ev = scene.time.addEvent({
    callback: () => {
      target.text += text[i];
      ++i;
      if (i === length) finish();
    },
    repeat: length - 1,
    delay: config?.delay || 25,
  });
  // Returned controller lets a scene reveal the whole line instantly on a click/space press (so a tap
  // skips the typewriter instead of doing nothing), firing the callback exactly once.
  return {
    get done() {
      return done;
    },
    skip: () => {
      if (done) return;
      ev.remove(false);
      finish();
    },
  };
}

export const CANNOT_READ_SIGN_TEXT = 'You cannot read the sign from this direction.';
export const SAMPLE_TEXT = 'Make sure you talk to npcs for helpful tips!';
export const LONG_SAMPLE_TEXT =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Enim sed faucibus turpis in eu mi bibendum.';
